import {
	DocumentSigningRoleType,
	type InvestmentProgrammeDocumentDetailsRes,
	type InvestmentProgrammeDocumentDetailsSignerRes,
} from '@dealstack/shared/lib/generated';
import { normaliseEmail } from '@dealstack/shared/lib/format/emailHelpers';

type InvestmentProgrammeDocumentDetailsSignerGroup =
	InvestmentProgrammeDocumentDetailsRes['groups'][number];

export type SignerWithRepresentative = InvestmentProgrammeDocumentDetailsSignerRes & {
	representative?: InvestmentProgrammeDocumentDetailsSignerRes;
	// The signing role the nested representative belongs to. Threaded here so a group-wide
	// toggle can fan out across every role in the visual group, not just the PARTY role.
	representativeRoleId?: string;
	piv?: InvestmentProgrammeDocumentDetailsSignerRes;
	// The signing role the nested PIV belongs to (see representativeRoleId).
	pivRoleId?: string;
};

export type SignerWithRepresentativeGroup = Omit<
	InvestmentProgrammeDocumentDetailsSignerGroup,
	'signers'
> & {
	signers: SignerWithRepresentative[];
};

/**
 * Every email already on one of this role's slots, normalized — whichever identity carries
 * it (a platform user's account email, or an email-invited Person's). Keeps the same human
 * from filling two slots on one role under a different identity (DC-1203).
 */
export function buildOccupiedEmailSet(signers: readonly SignerWithRepresentative[]): Set<string> {
	return new Set(
		signers
			.filter(
				(signer): signer is SignerWithRepresentative & { email: string } => !!signer.email
			)
			.map((signer) => normaliseEmail(signer.email))
	);
}

/** Whether the current user may claim this role's remaining open slot for themselves. */
export function canCurrentUserClaimCounterpartySlot({
	isCounterpartyGroup,
	currentUser,
	signers,
}: {
	isCounterpartyGroup: boolean;
	currentUser: { id: string; email: string } | undefined;
	signers: readonly SignerWithRepresentative[];
}): boolean {
	if (!isCounterpartyGroup || !currentUser) {
		return false;
	}
	if (signers.some((signer) => signer.assigned && signer.userId === currentUser.id)) {
		return false;
	}
	return !buildOccupiedEmailSet(signers).has(normaliseEmail(currentUser.email));
}

export const NESTED_ROLE_TYPES = new Set([
	DocumentSigningRoleType.WITNESS,
	DocumentSigningRoleType.PIV,
	DocumentSigningRoleType.REPRESENTATIVE,
]);

export function createSignerWithRepresentativeGroups(
	groups: InvestmentProgrammeDocumentDetailsSignerGroup[]
): SignerWithRepresentativeGroup[] {
	type NestedSigner = { signer: InvestmentProgrammeDocumentDetailsSignerRes; roleId: string };
	const pivSignersByInvestorId = new Map<string, NestedSigner>();
	const representativesByInvestorId = new Map<string, NestedSigner>();

	for (const group of groups) {
		if (group.groupType === DocumentSigningRoleType.PIV) {
			for (const signer of group.signers) {
				if (signer.investorId) {
					pivSignersByInvestorId.set(signer.investorId, { signer, roleId: group.roleId });
				}
			}
		}
		if (group.groupType === DocumentSigningRoleType.REPRESENTATIVE) {
			for (const signer of group.signers) {
				if (signer.investorId) {
					representativesByInvestorId.set(signer.investorId, {
						signer,
						roleId: group.roleId,
					});
				}
			}
		}
	}

	return groups
		.filter((group) => !NESTED_ROLE_TYPES.has(group.groupType))
		.map((group) => ({
			...group,
			signers: group.signers.map((signer): SignerWithRepresentative => {
				const investorId = signer.investorId;
				const representative = investorId
					? representativesByInvestorId.get(investorId)
					: undefined;
				const piv = investorId ? pivSignersByInvestorId.get(investorId) : undefined;

				return {
					...signer,
					representative: representative?.signer,
					representativeRoleId: representative?.roleId,
					piv: piv?.signer,
					pivRoleId: piv?.roleId,
				};
			}),
		}));
}
